import { useI18n } from 'hooks/useI18n';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type {
  SalesItem,
  SalesItemAttributes,
  SalesProps
} from '../../BattleReportComponent.types';
import { StyledChart } from './BattleReportGoals.styled';

type dataItem = {
  month: string;
  sales: number | null;
  turnover: number | null;
};

const GoalsChart: React.FC<SalesProps> = ({ sales }) => {
  const { locale } = useRouter();
  const { t } = useI18n('battle-report');

  const filterMonth = (
    year: number,
    month: number,
    property: keyof SalesItemAttributes
  ): number | null => {
    const filteredData = sales.filter((obj: SalesItem) => {
      if (obj.attributes.year === year && obj.attributes.month === month) {
        return obj;
      }
    });

    return filteredData?.length
      ? (filteredData[0].attributes[property] as number)
      : null;
  };

  const getMonthName = (monthNumber: number | null): string => {
    if (!monthNumber) {
      return '';
    }
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return date.toLocaleString(locale, { month: 'short' });
  };

  const chartData = (): dataItem[] => {
    const a = [];
    for (let i = 0; i < 12; i++) {
      a.push({
        month: getMonthName(i + 1),
        sales: filterMonth(2023, i + 1, 'sales'),
        turnover: filterMonth(2023, i + 1, 'billing')
      });
    }
    return a;
  };

  const [chartDesktop, setChartDesktop] = useState<boolean>(true);

  useEffect(() => {
    function isChartDesktop(): void {
      setChartDesktop(window.matchMedia('(min-width: 1056px)').matches);
    }

    isChartDesktop();

    window.addEventListener('resize', isChartDesktop);

    return () => {
      window.removeEventListener('resize', isChartDesktop);
    };
  }, []);

  return (
    <div>
      <StyledChart>
        <ResponsiveContainer width="99%" height={chartDesktop ? 250 : 500}>
          <BarChart
            data={chartData()}
            layout={chartDesktop ? 'horizontal' : 'vertical'}
          >
            {chartDesktop && (
              <>
                <XAxis
                  type="category"
                  dataKey="month"
                  tickLine={false}
                  interval={0}
                  axisLine={true}
                />
                <YAxis
                  tickLine={false}
                  width={60}
                  axisLine={true}
                  tickMargin={5}
                  tickFormatter={(tick: number) => {
                    return `€${(
                      Math.sign(tick) *
                      (Math.abs(tick) / 1000)
                    ).toFixed(0)}K`;
                  }}
                />
              </>
            )}
            {!chartDesktop && (
              <>
                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={true}
                  tickMargin={5}
                  tickFormatter={(tick: number) => {
                    return `€${(
                      Math.sign(tick) *
                      (Math.abs(tick) / 1000)
                    ).toFixed(0)}K`;
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="month"
                  tickLine={false}
                  interval={0}
                  axisLine={true}
                />
              </>
            )}
            <Tooltip
              cursor={{ fill: 'transparent' }}
              labelStyle={{ display: 'none' }}
              separator=": "
              formatter={(value: number) => `${value.toLocaleString(locale)} €`}
              wrapperClassName="tooltip-x"
            />
            <Bar
              dataKey="sales"
              barSize={8}
              radius={chartDesktop ? [5, 5, 0, 0] : [0, 5, 5, 0]}
              fill="#8DD3C7"
              name={t('battle-report.objectives.sales')}
            />
            <Bar
              dataKey="turnover"
              barSize={8}
              radius={chartDesktop ? [5, 5, 0, 0] : [0, 5, 5, 0]}
              fill="#A6D855"
              name={t('battle-report.objectives.turnover')}
            ></Bar>
          </BarChart>
        </ResponsiveContainer>
      </StyledChart>
    </div>
  );
};

export default GoalsChart;
